import {gql} from '@apollo/client';
import {isSubset} from '@benchling/js-utils';
import React, {useMemo, useState} from 'react';

import * as analytics from 'analytics/Analytics';
import {assert} from 'Asserts';
import BenchlingConfig from 'BenchlingConfig';
import {UPDATE_FLOWCHART_MODAL_TYPE} from 'constants/analytics-products/WorkflowAnalyticsConstants';
import {
  ActionMethod,
  CloseType,
  ANALYTICS_MODULE,
  COMMON_ANALYTICS_ACTIONS,
  ActionOrigin,
  WORKFLOWS_ANALYTICS_ACTIONS,
} from 'constants/AnalyticsConstants';
import BenchlingQuery from 'graphql-client/BenchlingQuery';
import {getFragmentWrapper} from 'graphql-client/declarative-translations/external/getFragmentWrapper';
import ExtractByTypename from 'graphql-client/ExtractByTypename';
import {isInaccessible} from 'graphql-client/InaccessibleInfo';
import notifications from 'notifications';
import * as ProcessManagementDAO from 'processes/dao/ProcessManagementDAO';
import {FLOWCHART_PROCESS_DATA_FRAGMENT} from 'processes/graph-builder-processes/flowchart-viz/LiveUpdatingTaskGroupFlowchartWrapper';
import ProcessFlowchartEditor from 'processes/graph-builder-processes/flowchart-viz/ProcessFlowchartEditor';
import {ProcessInteractiveFlowchart} from 'processes/graph-builder-processes/flowchart-viz/ProcessFlowchartVizConstants';
import {
  ClientFlowchartConfig,
  convertGQLFlowchartConfigToClient,
} from 'processes/graph-builder-processes/flowchart-viz/util/convertGQLFlowchartConfigToClient';
import convertProcessInteractiveFlowchartToFlowchartSpec from 'processes/graph-builder-processes/flowchart-viz/util/convertProcessInteractiveFlowchartToFlowchartSpec';
import {flowchartHasChainedRouters} from 'processes/graph-builder-processes/flowchart-viz/util/flowchartHasChainedRouters';
import {
  getEdgeHash,
  getTaskGroupLineageSummary,
  mergeTaskGroupLineageIntoFlowchart,
} from 'processes/graph-builder-processes/flowchart-viz/util/mergeTaskGroupLineageIntoFlowchart';
import constructDesignSpace from 'processes/graph-builder-processes/util/constructDesignSpace';
import {FlowchartValidationErrorType} from 'processes/graph-builder-processes/validation/ProcessFlowchartForValidationTypes';
import {AccessibleFlowchartConfigVersion} from 'processes/settings/graph-builder-flowchart-config/FlowchartConfigConstants';
import ExecutionFlowchartPane from 'processes/settings/graph-builder-flowchart-config/horizontal-flowchart/execution/ExecutionFlowchartPane';
import {FlowchartConfigWithMetadata} from 'processes/settings/graph-builder-flowchart-config/horizontal-flowchart/redux/flowchartConfigAutosaveManagerCallbacks';
import isFlowchartConfigVersionFullyAccessible from 'processes/settings/graph-builder-flowchart-config/isFlowchartConfigVersionFullyAccessible';
import {ButtonStyle} from 'ui-library/buttons/Button';
import {CenteredHorizontalThrobber} from 'ui-library/HorizontalThrobber';
import Modal from 'ui-library/modals/Modal';

import {ProcessTaskSchemaGraphQL} from '../../../graphql-fragments/processes/schemas/__generated/ProcessTaskSchemaGraphQL';
import {FlowchartEditorMode} from '../graph-builder-processes/flowchart-viz/horizontal-flowchart/ProcessFlowchartEditorModeConstants';
import {convertFlowchartGqlToProcessInteractiveFlowchart} from '../graph-builder-processes/flowchart-viz/util/convertFlowchartGqlToProcessFlowchart';
import {
  ProcessFlowchartRootProcessQuery,
  ProcessFlowchartRootProcessQueryVariables,
} from './__generated/ProcessFlowchartRootProcessQuery';
import {
  ProcessFlowchartTaskGroupModificationSourceDataQuery,
  ProcessFlowchartTaskGroupModificationSourceDataQueryVariables,
} from './__generated/ProcessFlowchartTaskGroupModificationSourceDataQuery';
import {
  FlowchartTaskOrSchemaInaccessibleError,
  PROCESS_FLOWCHART_ROOT_PROCESS_QUERY,
} from './ProcessFlowchartModalShared';

type ProcessFlowchart = ExtractByTypename<
  ProcessFlowchartTaskGroupModificationSourceDataQuery['processFlowchart'],
  'ProcessFlowchartNode'
>;

type FlowchartModificationSourceDataFlowchartProcess = ExtractByTypename<
  ProcessFlowchartTaskGroupModificationSourceDataQuery['process'],
  'ProcessNode'
>;

type ProcessFlowchartTask = ExtractByTypename<
  ProcessFlowchartRootProcessQuery['process'],
  'ProcessNode'
>['tasks'][number];

type ProcessFlowchartTaskSchema = ExtractByTypename<
  ExtractByTypename<ProcessFlowchartRootProcessQuery['process'], 'ProcessNode'>['taskSchemaOrInaccessible'],
  'ProcessTaskSchemaNode'
>;

export const PROCESS_FLOWCHART_TASK_GROUP_MODIFICATION_SOURCE_DATA_QUERY = gql`
  # $rootTaskId is unused
  # eslint-disable @graphql-eslint/no-unused-variables
  query ProcessFlowchartTaskGroupModificationSourceDataQuery(
    $flowchartId: StrictString!
    $flowchartProcessId: StrictString!
    $nodeTaskSchemaIds: [StrictString!]!
    $rootTaskId: StrictString
  ) @skipAudit @engComponent(name: WORKFLOWS) {
    processFlowchart(id: $flowchartId) {
      ... on ProcessFlowchartNode {
        ...ProcessFlowchartGraphQL
      }
      ...InaccessibleInfoGraphQL
    }
    process(id: $flowchartProcessId) {
      ... on ProcessNode {
        ...FlowchartProcessDataInfo
        childProcesses {
          ... on ProcessNode {
            ...FlowchartProcessDataInfo
          }
          ...InaccessibleInfoGraphQL
        }
        isMappingInProgressForFlowchartRootedAtProcess
        hasTasksWithFailedMappings
      }
      ...InaccessibleInfoGraphQL
    }
    processTaskSchemasSupportingPlatformFeature(platformFeature: SCHEMA_CONFIG, ids: $nodeTaskSchemaIds) {
      ... on ProcessTaskSchemaNode {
        ...ProcessTaskSchemaGraphQL
      }
    }
  }
  ${getFragmentWrapper('ProcessFlowchart').fragment}
  ${getFragmentWrapper('ProcessTaskSchema').fragment}
  ${getFragmentWrapper('InaccessibleInfo').fragment}
  ${FLOWCHART_PROCESS_DATA_FRAGMENT}
`;

export interface ProcessUpdateFlowchartModalProps {
  flowchartProcessId: string;
  sourceType: string;
  eventGroupId: string;
  actionOrigin: ActionOrigin;
  onFlowchartModified: () => void;
  onRequestClose: () => void;
  onRequestSubmit: () => void;
}

export interface QueryingProcessUpdateFlowchartModalContentsProps extends ProcessUpdateFlowchartModalProps {
  onFlowchartChange: (args: {
    flowchart: ProcessInteractiveFlowchart | null;
    flowchartErrors: Array<FlowchartValidationErrorType>;
    flowchartConfig: ClientFlowchartConfig;
    flowchartTaskSchema: ProcessTaskSchemaGraphQL;
    flowchartData: FlowchartModificationSourceDataFlowchartProcess | null;
  }) => void;
}

interface ValidatedFlowchartInfo {
  flowchartId: string;
  flowchartTasks: Array<ProcessFlowchartTask>;
  flowchartTaskSchema: ProcessFlowchartTaskSchema;
  flowchartConfigVersion: AccessibleFlowchartConfigVersion;
}

export default function ProcessUpdateFlowchartModal(props: ProcessUpdateFlowchartModalProps): JSX.Element {
  const [flowchart, setFlowchart] = useState<ProcessInteractiveFlowchart | null>(null);
  const [flowchartErrors, setFlowchartErrors] = useState<Array<FlowchartValidationErrorType>>([]);
  const [flowchartTaskSchema, setFlowchartTaskSchema] = useState<ProcessTaskSchemaGraphQL | null>(null);
  const [flowchartData, setFlowchartData] = useState<FlowchartModificationSourceDataFlowchartProcess | null>(
    null
  );
  const [flowchartConfig, setFlowchartConfig] = useState<ClientFlowchartConfig | null>(null);
  const isSubmitEnabled =
    flowchart != null &&
    flowchartErrors.length === 0 &&
    flowchartTaskSchema != null &&
    flowchartData != null &&
    flowchartConfig != null;
  const isImprovedEditorEnabled = BenchlingConfig.WORKFLOWS_IMPROVED_ACM_PCM_EDITOR;

  return (
    <Modal
      title="Update flowchart"
      buttons={[
        {
          text: 'Cancel',
          cancel: true,
          style: ButtonStyle.TERTIARY_GRAY,
          onClick: () => {
            analytics.trackEventWithModule(
              COMMON_ANALYTICS_ACTIONS.CLOSED_MODAL,
              ANALYTICS_MODULE.WORKFLOWS,
              {
                event_group_id: props.eventGroupId,
                action_origin: props.actionOrigin,
                action_method: ActionMethod.POST_CREATION_FLOWCHART_MODIFICATION,
                task_schema_api_id: flowchartTaskSchema?.id,
                close_type: CloseType.CANCEL,
                error_types: flowchartErrors,
                modal_type: UPDATE_FLOWCHART_MODAL_TYPE,
              }
            );
            props.onRequestClose();
          },
        },
        {
          text: 'Submit',
          submit: true,
          style: ButtonStyle.PRIMARY,
          disabled: !isSubmitEnabled,
        },
      ]}
      onRequestSubmit={async () => {
        assert(
          flowchart != null &&
            flowchartConfig != null &&
            flowchartTaskSchema != null &&
            flowchartData != null,
          'Flowchart data must be defined'
        );

        const validationMessage = getIsFlowchartModificationValidErrorMessage(flowchart, flowchartData);
        if (validationMessage != null) {
          notifications.error(validationMessage);
          return;
        }
        const flowchartSpec = convertProcessInteractiveFlowchartToFlowchartSpec(
          flowchart,
          flowchartConfig.schemaMapperConfigs.map(
            ({clientId, fromTaskSchemas, toTaskSchema, mapperConfig}) => {
              assert(
                fromTaskSchemas.length > 0 && toTaskSchema != null && mapperConfig != null,
                'Expected schema mapper to be defined'
              );
              return {
                clientId,
                fromTaskSchemaIds: fromTaskSchemas.map(({id}) => id),
                toTaskSchemaId: toTaskSchema.id,
                mapperConfig,
              };
            }
          ),
          flowchartTaskSchema
        );

        await ProcessManagementDAO.updateProcess({
          id: props.flowchartProcessId,
          flowchartSpec,
          sourceType: props.sourceType,
          actionOrigin: props.actionOrigin,
          actionMethod: ActionMethod.POST_CREATION_FLOWCHART_MODIFICATION,
          eventGroupId: props.eventGroupId,
        });
        props.onFlowchartModified();
        analytics.trackEventWithModule(COMMON_ANALYTICS_ACTIONS.SUBMITTED_MODAL, ANALYTICS_MODULE.WORKFLOWS, {
          event_group_id: props.eventGroupId,
          action_origin: props.actionOrigin,
          action_method: ActionMethod.POST_CREATION_FLOWCHART_MODIFICATION,
          task_schema_api_id: flowchartTaskSchema.id,
          modal_type: UPDATE_FLOWCHART_MODAL_TYPE,
        });
        props.onRequestSubmit();
      }}
      onRequestClose={() => {
        analytics.trackEventWithModule(COMMON_ANALYTICS_ACTIONS.CLOSED_MODAL, ANALYTICS_MODULE.WORKFLOWS, {
          event_group_id: props.eventGroupId,
          action_origin: props.actionOrigin,
          action_method: ActionMethod.POST_CREATION_FLOWCHART_MODIFICATION,
          task_schema_api_id: flowchartTaskSchema?.id,
          close_type: CloseType.EXIT,
          error_types: flowchartErrors,
          modal_type: UPDATE_FLOWCHART_MODAL_TYPE,
        });
        props.onRequestClose();
      }}
      closeableOnEscapeKey={false}
      extraLarge={true}
      fullHeight={true}
      scrollable={false}
      bodyPadded={!isImprovedEditorEnabled}
      flexContent={isImprovedEditorEnabled}
    >
      <QueryingProcessUpdateFlowchartModalContents
        {...props}
        onFlowchartChange={({
          flowchart,
          flowchartErrors,
          flowchartConfig,
          flowchartTaskSchema,
          flowchartData,
        }) => {
          setFlowchart(flowchart);
          setFlowchartErrors(flowchartErrors);
          setFlowchartConfig(flowchartConfig);
          setFlowchartTaskSchema(flowchartTaskSchema);
          setFlowchartData(flowchartData);
        }}
      />
    </Modal>
  );
}

interface ValidatedTaskGroupFlowchartStructureData {
  processFlowchart: ProcessFlowchart;
  process: FlowchartModificationSourceDataFlowchartProcess;
  processTaskSchemas: Array<ProcessTaskSchemaGraphQL>;
}

function validateFlowchartTaskGroupStructureData(
  flowchartStructureData: ProcessFlowchartTaskGroupModificationSourceDataQuery
): ValidatedTaskGroupFlowchartStructureData | null {
  const {processFlowchart, process, processTaskSchemasSupportingPlatformFeature} = flowchartStructureData;
  if (isInaccessible(processFlowchart) || isInaccessible(process)) {
    return null;
  }
  return {processFlowchart, process, processTaskSchemas: processTaskSchemasSupportingPlatformFeature};
}

function QueryingProcessUpdateFlowchartModalContents(
  props: QueryingProcessUpdateFlowchartModalContentsProps
): JSX.Element {
  // TODO: PR 229196 routes WORKFLOWS_IMPROVED_ACM_PCM_EDITOR to ImprovedProcessUpdateFlowchartModal; drop this draft state and pane branch there.
  const [draftState, setDraftState] = useState<FlowchartConfigWithMetadata | null>(null);

  if (BenchlingConfig.WORKFLOWS_IMPROVED_ACM_PCM_EDITOR) {
    return (
      <ExecutionFlowchartPane
        editorMode={FlowchartEditorMode.PCM_EDITOR}
        processId={props.flowchartProcessId}
        draftState={draftState}
        setDraftState={setDraftState}
      />
    );
  }

  return (
    <BenchlingQuery<ProcessFlowchartRootProcessQuery, ProcessFlowchartRootProcessQueryVariables>
      query={PROCESS_FLOWCHART_ROOT_PROCESS_QUERY}
      variables={{flowchartProcessId: props.flowchartProcessId}}
      renderLoading={() => <CenteredHorizontalThrobber />}
    >
      {(flowchartTaskData: ProcessFlowchartRootProcessQuery) => {
        const validatedFlowchartInfo = validateAndGetFlowchartInfo(flowchartTaskData);

        if (validatedFlowchartInfo == null) {
          return <FlowchartTaskOrSchemaInaccessibleError />;
        }

        return (
          <BenchlingQuery<
            ProcessFlowchartTaskGroupModificationSourceDataQuery,
            ProcessFlowchartTaskGroupModificationSourceDataQueryVariables
          >
            query={PROCESS_FLOWCHART_TASK_GROUP_MODIFICATION_SOURCE_DATA_QUERY}
            variables={{
              flowchartId: validatedFlowchartInfo.flowchartId,
              flowchartProcessId: props.flowchartProcessId,
              nodeTaskSchemaIds: validatedFlowchartInfo.flowchartConfigVersion.availableSchemas.map(
                ({id}) => id
              ),
            }}
            renderLoading={() => <CenteredHorizontalThrobber />}
          >
            {(flowchartStructureData: ProcessFlowchartTaskGroupModificationSourceDataQuery) => {
              const validatedFlowchartStructureData =
                validateFlowchartTaskGroupStructureData(flowchartStructureData);
              if (validatedFlowchartStructureData == null) {
                return <FlowchartTaskOrSchemaInaccessibleError />;
              }
              const interactiveFlowchart = convertFlowchartGqlToProcessInteractiveFlowchart(
                validatedFlowchartStructureData.processFlowchart
              );

              const flowchartWithLineageData = mergeTaskGroupLineageIntoFlowchart({
                flowchart: interactiveFlowchart,
                rootProcess: validatedFlowchartStructureData.process,
              });

              return (
                <ProcessUpdateFlowchartModalContents
                  flowchartBasicInfo={validatedFlowchartInfo}
                  processFlowchart={flowchartWithLineageData}
                  processTaskSchemas={validatedFlowchartStructureData.processTaskSchemas}
                  onFlowchartChange={(args) => {
                    props.onFlowchartChange({
                      ...args,
                      flowchartData: validatedFlowchartStructureData.process,
                    });
                  }}
                  eventGroupId={props.eventGroupId}
                />
              );
            }}
          </BenchlingQuery>
        );
      }}
    </BenchlingQuery>
  );
}

function ProcessUpdateFlowchartModalContents({
  processFlowchart,
  processTaskSchemas,
  flowchartBasicInfo,
  onFlowchartChange,
  eventGroupId,
}: {
  processFlowchart: ProcessInteractiveFlowchart;
  processTaskSchemas: Array<ProcessTaskSchemaGraphQL>;
  flowchartBasicInfo: ValidatedFlowchartInfo;
  onFlowchartChange: (args: {
    flowchart: ProcessInteractiveFlowchart | null;
    flowchartErrors: Array<FlowchartValidationErrorType>;
    flowchartConfig: ClientFlowchartConfig;
    flowchartTaskSchema: ProcessTaskSchemaGraphQL;
  }) => void;
  eventGroupId: string;
}): JSX.Element {
  const {initialFlowchart, designSpace, flowchartConfig} = useMemo(() => {
    const flowchartConfig = convertGQLFlowchartConfigToClient(
      flowchartBasicInfo.flowchartConfigVersion,
      processTaskSchemas,
      flowchartBasicInfo.flowchartTaskSchema
    );
    const designSpace = constructDesignSpace(
      processTaskSchemas,
      flowchartConfig.schemaMapperConfigs,
      flowchartBasicInfo.flowchartTaskSchema
    );

    return {initialFlowchart: processFlowchart, designSpace, flowchartConfig};
  }, [
    processTaskSchemas,
    processFlowchart,
    flowchartBasicInfo.flowchartTaskSchema,
    flowchartBasicInfo.flowchartConfigVersion,
  ]);

  const [didInitialFlowchartHaveChainedRouters, _] = useState(flowchartHasChainedRouters(initialFlowchart));

  if (didInitialFlowchartHaveChainedRouters) {
    analytics.trackEvent(WORKFLOWS_ANALYTICS_ACTIONS.OPENED_FLOWCHART_WITH_CHAINED_ROUTERS, {
      event_group_id: eventGroupId,
      modal_type: UPDATE_FLOWCHART_MODAL_TYPE,
    });
  }

  return (
    <ProcessFlowchartEditor
      designSpace={designSpace}
      flowchart={initialFlowchart}
      editorEventGroupId={eventGroupId}
      actionMethod={ActionMethod.POST_CREATION_FLOWCHART_MODIFICATION}
      didInitialFlowchartHaveChainedRouters={didInitialFlowchartHaveChainedRouters}
      modalType={UPDATE_FLOWCHART_MODAL_TYPE}
      onFlowchartChange={(flowchart, flowchartErrors) => {
        onFlowchartChange({
          flowchart,
          flowchartErrors,
          flowchartConfig,
          flowchartTaskSchema: flowchartBasicInfo.flowchartTaskSchema,
        });
      }}
    />
  );
}

function validateAndGetFlowchartInfo(
  flowchartTaskData: ProcessFlowchartRootProcessQuery
): ValidatedFlowchartInfo | null {
  if (isInaccessible(flowchartTaskData.process)) {
    return null;
  }
  const flowchartTasks = flowchartTaskData.process.tasks;
  assert(flowchartTasks.length > 0, 'Must have at least 1 task');

  const flowchartTaskSchema = flowchartTaskData.process.taskSchemaOrInaccessible;
  if (isInaccessible(flowchartTaskSchema)) {
    return null;
  }

  const executionFlowchartIds = flowchartTasks.map((flowchartTask) => flowchartTask.executionFlowchart?.id);
  assert(
    executionFlowchartIds.every((id) => id != null),
    'Expected all tasks to have an execution flowchart'
  );
  assert(
    executionFlowchartIds.every((flowchartId) => flowchartId === executionFlowchartIds[0]),
    'Expected all tasks to have same execution flowchart'
  );

  const flowchartConfigVersion = flowchartTaskData.process.flowchartConfigVersion;
  assert(flowchartConfigVersion != null, 'Expect flowchart task group to have a flowchart config version');
  if (!isFlowchartConfigVersionFullyAccessible(flowchartConfigVersion)) {
    return null;
  }

  return {
    flowchartId: executionFlowchartIds[0],
    flowchartTaskSchema,
    flowchartTasks,
    flowchartConfigVersion,
  };
}

function getIsFlowchartModificationValidErrorMessage(
  flowchart: ProcessInteractiveFlowchart,
  flowchartData: FlowchartModificationSourceDataFlowchartProcess
): string | null {
  const nodeIds = new Set(flowchart.nodes.map(({clientId}) => clientId));

  const {edgeHashes: edgesExpectedToExist, nodeIdsWithData} = getTaskGroupLineageSummary(
    flowchart,
    flowchartData
  );

  if (!isSubset(nodeIdsWithData, nodeIds)) {
    return 'Cannot remove nodes which have tasks.';
  }
  const edgeReferences = new Set(
    flowchart.edges.map((edge) => getEdgeHash(edge.fromNodeClientId, edge.toNodeClientId))
  );
  if (!isSubset(edgesExpectedToExist, edgeReferences)) {
    return 'Cannot remove edges which are used in the task lineage.';
  }
  return null;
}
